import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  FileInput,
  Group,
  Loader,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconArrowLeft,
  IconCalendarStats,
  IconFilePlus,
  IconPaperclip,
  IconSparkles,
  IconTrash,
} from '@tabler/icons-react';
import {
  QuarterlyReport,
  useGenerateQuarterly,
  useCreateEmptyQuarterly,
  useDeleteQuarterly,
  useMyQuarterlyReports,
  useQuarterlyReport,
  useSubmitQuarterly,
  useUnsubmitQuarterly,
  useReturnQuarterly,
} from '../../hooks/useQuarterlyApi';
import { QuarterlyEditor } from '../../components/essays/QuarterlyEditor';
import { LoadFailed } from '../../components/ui/LoadFailed';
import { reportingQuarter } from '../../components/essays/quarterPeriod';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const quarterLabel = (year: number, quarter: number, t: Translate) =>
  t('quarterly.label', { quarter, year });

/** Открытый отчёт: редактор плюс кнопки сборки и смены статуса. */
const QuarterlyDetail: React.FC<{ id: string; onBack: () => void }> = ({ id, onBack }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuarterlyReport(id);
  const generate = useGenerateQuarterly();
  const submit = useSubmitQuarterly();
  const unsubmit = useUnsubmitQuarterly();
  const returnReport = useReturnQuarterly();

  const onError = (error: unknown) =>
    showToast({
      title: t('common.error'),
      message: getErrorMessage(error, t('essays.saveError')),
      color: 'red',
    });

  if (isError && !data) return <LoadFailed onBack={onBack} />;
  if (isLoading || !data) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const report = data.report;

  // Пересборка затирает дерево целиком, включая правки руками, — спрашиваем.
  // Первая сборка ничего не теряет, поэтому идёт без вопроса.
  const runGenerate = () => {
    const start = () =>
      generate.mutate({ year: report.year, quarter: report.quarter }, { onError });
    if ((report.items || []).length === 0) return start();
    modals.openConfirmModal({
      title: t('quarterly.regenerate'),
      centered: true,
      children: <Text size="sm">{t('quarterly.regenerateConfirm')}</Text>,
      labels: { confirm: t('quarterly.regenerate'), cancel: t('common.cancel') },
      onConfirm: start,
    });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          {t('common.back')}
        </Button>
        <Text fw={600}>{quarterLabel(report.year, report.quarter, t)}</Text>
      </Group>
      <QuarterlyEditor
        key={report._id}
        report={report}
        readOnly={!data.canEdit}
        canSubmit
        isGenerating={generate.isPending}
        isStatusPending={submit.isPending || unsubmit.isPending}
        onGenerate={runGenerate}
        canReturn={data.canReturn}
        isScored={data.isScored}
        isReturnPending={returnReport.isPending}
        onReturn={(comment) => returnReport.mutateAsync({ id, comment })}
        onSubmit={() => submit.mutate(id, { onError })}
        onUnsubmit={() => unsubmit.mutate(id, { onError })}
      />
    </Stack>
  );
};

/**
 * Список квартальных отчётов человека. Отчёт заводится сборкой из эссе или
 * пустым — для тех, кто пишет руками.
 */
const MAX_FILES = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

interface QuarterlyTabProps {
  /**
   * Место в шапке страницы для кнопок создания. Кнопки месячной вкладки стоят
   * там же — без этого при переключении «Месяц / Квартал» они прыгали вниз.
   */
  headerActions: HTMLElement | null;
}

export const QuarterlyTab: React.FC<QuarterlyTabProps> = ({ headerActions }) => {
  const { t } = useTranslation();
  const { data: reports, isLoading } = useMyQuarterlyReports();
  const generate = useGenerateQuarterly();
  const createEmpty = useCreateEmptyQuarterly();
  const remove = useDeleteQuarterly();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  // Одна модалка выбора квартала на оба действия: собрать из эссе или завести пустой.
  const [isEmptyMode, setIsEmptyMode] = useState(false);
  // Приложенные Word-эссе — их текст уходит в сборку вместе с эссе из системы.
  const [files, setFiles] = useState<File[]>([]);
  // Те же пределы, что на сервере: лучше сказать сразу, чем после отправки.
  const filesError =
    files.length > MAX_FILES
      ? t('quarterly.filesTooMany', { count: MAX_FILES })
      : files.some((f) => f.size > MAX_FILE_BYTES)
        ? t('quarterly.filesTooBig')
        : null;

  const [year, setYear] = useState(() => String(reportingQuarter().year));
  const [quarter, setQuarter] = useState(() => String(reportingQuarter().quarter));

  // Селекты переживают закрытие модалки: выбрал человек другой квартал один раз
  // — и следующее «Собрать» открылось бы на нём, мимо отчётного.
  const openCreate = (isEmpty: boolean) => {
    setIsEmptyMode(isEmpty);
    setFiles([]);
    const period = reportingQuarter();
    setYear(String(period.year));
    setQuarter(String(period.quarter));
    open();
  };

  const quarterOptions = useMemo(
    () =>
      [1, 2, 3, 4].map((q) => ({ value: String(q), label: t('quarterly.quarterOption', { q }) })),
    [t],
  );
  const yearOptions = useMemo(() => {
    const y = dayjs().year();
    return Array.from({ length: 5 }, (_, i) => String(y - 3 + i));
  }, []);

  // Удаление спрашивают: отчёт собирается нейронкой и дописывается руками,
  // и «собери заново» вернёт не то же самое.
  const handleDelete = (e: React.MouseEvent, report: QuarterlyReport) => {
    e.stopPropagation();
    modals.openConfirmModal({
      title: t('quarterly.confirmDelete'),
      centered: true,
      children: <Text size="sm">{t('quarterly.confirmDeleteBody')}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        remove.mutate(report._id, {
          onError: (error) =>
            showToast({
              title: t('common.error'),
              message: getErrorMessage(error, t('essays.saveError')),
              color: 'red',
            }),
        }),
    });
  };

  const runGenerate = (y: number, q: number) => {
    generate.mutate(
      { year: y, quarter: q, files },
      {
        onSuccess: (created) => {
          if (created?.id) setSelectedId(created.id);
          close();
          if (created?.isTruncated) {
            showToast({
              title: t('quarterly.filesTruncatedTitle'),
              message: t('quarterly.filesTruncated'),
              color: 'yellow',
            });
          }
        },
        onError: (error) => {
          close();
          showToast({
            title: t('common.error'),
            message: getErrorMessage(error, t('quarterly.nothingToBuild')),
            color: 'red',
          });
        },
      },
    );
  };

  const handleGenerate = () => {
    const y = Number(year);
    const q = Number(quarter);
    const existing = reports?.find((r) => r.year === y && r.quarter === q);
    if (!existing) return runGenerate(y, q);
    if (files.length > 0 && existing.status === 'submitted') {
      showToast({
        title: t('common.error'),
        message: t('quarterly.submittedNoRebuild'),
        color: 'red',
      });
      return;
    }
    // Без файлов — просто открыть существующий. С файлами человек пришёл
    // пересобрать: предупреждаем, что текст заменится целиком.
    if (files.length === 0) {
      setSelectedId(existing._id);
      close();
      return;
    }
    modals.openConfirmModal({
      title: t('quarterly.regenerate'),
      children: <Text size="sm">{t('quarterly.regenerateConfirm')}</Text>,
      labels: { confirm: t('quarterly.regenerate'), cancel: t('common.cancel') },
      onConfirm: () => runGenerate(y, q),
    });
  };

  const handleCreateEmpty = () => {
    createEmpty.mutate(
      { year: Number(year), quarter: Number(quarter) },
      {
        onSuccess: ({ id }) => {
          setSelectedId(id);
          close();
        },
        onError: (error) => {
          close();
          showToast({
            title: t('common.error'),
            message: getErrorMessage(error, t('quarterly.createEmptyFailed')),
            color: 'red',
          });
        },
      },
    );
  };

  const createButtons = (
    <Group gap="xs">
      <Button
        variant="default"
        leftSection={<IconFilePlus size={16} />}
        onClick={() => openCreate(true)}
      >
        {t('quarterly.createEmpty')}
      </Button>
      <Button leftSection={<IconSparkles size={16} />} onClick={() => openCreate(false)}>
        {t('quarterly.generate')}
      </Button>
    </Group>
  );

  if (selectedId) {
    return <QuarterlyDetail id={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <>
      {headerActions && createPortal(createButtons, headerActions)}

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : !reports || reports.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="sm">
            <IconCalendarStats size={48} opacity={0.4} />
            <Text c="dimmed">{t('quarterly.emptyList')}</Text>
            {createButtons}
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {reports.map((report: QuarterlyReport) => (
            <Card
              key={report._id}
              withBorder
              radius="md"
              padding="md"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedId(report._id)}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  <IconCalendarStats size={20} />
                  <Text fw={600}>{quarterLabel(report.year, report.quarter, t)}</Text>
                </Group>
                {/* Сданный отчёт сначала возвращают в черновик, а оценённый не
                    удаляется вовсе — красная корзина, которая всегда отвечает
                    отказом, тут была бы обманом. */}
                {report.status !== 'submitted' && (
                  <Tooltip label={t('common.delete')} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={(e) => handleDelete(e, report)}
                      aria-label={t('common.delete')}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
              <Group gap="xs" mt="sm">
                <Badge variant="light" color={report.status === 'submitted' ? 'teal' : 'gray'}>
                  {t(`essays.status.${report.status}`)}
                </Badge>
                {report.generation.state === 'running' && (
                  <Badge variant="light" color="blue">
                    {t('quarterly.generating')}
                  </Badge>
                )}
                {report.generation.state === 'failed' && (
                  <Badge variant="light" color="red">
                    {t('quarterly.generateFailed')}
                  </Badge>
                )}
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <Modal opened={opened} onClose={close} title={t('quarterly.newReport')} centered>
        <Stack>
          <Select
            label={t('quarterly.quarter')}
            data={quarterOptions}
            value={quarter}
            onChange={(v) => v && setQuarter(v)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
          <Select
            label={t('essays.year')}
            data={yearOptions}
            value={year}
            onChange={(v) => v && setYear(v)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
          <Text size="xs" c="dimmed">
            {t(isEmptyMode ? 'quarterly.createEmptyHint' : 'quarterly.generateHint')}
          </Text>
          {!isEmptyMode && (
            <FileInput
              multiple
              clearable
              accept=".docx"
              value={files}
              onChange={setFiles}
              leftSection={<IconPaperclip size={16} />}
              label={t('quarterly.filesLabel')}
              description={t('quarterly.filesHint')}
              placeholder={t('quarterly.filesPlaceholder')}
              error={filesError}
            />
          )}
          {isEmptyMode ? (
            <Button onClick={handleCreateEmpty} loading={createEmpty.isPending} fullWidth>
              {t('quarterly.createEmpty')}
            </Button>
          ) : (
            <Button
              onClick={handleGenerate}
              loading={generate.isPending}
              disabled={filesError !== null}
              fullWidth
            >
              {t('quarterly.generate')}
            </Button>
          )}
        </Stack>
      </Modal>
    </>
  );
};
